/**
 * FullVSL overlay configuration UI editor (FUL-11).
 *
 * Form-based builder for the six MVP overlay moments (FUL-10 templates). Mirrors
 * the Overlays-tab flow (US-6.2, FUL-10 §9): a moment list with enabled/disabled
 * toggles, and a grouped detail form (Conteúdo · Aparência · Momento · Ações)
 * with a 7-slot safe-zone position picker, custom asset URL + assetType, and live
 * field validation. Emits the resulting `Overlay[]` on every change so the player
 * preview can re-render in real time.
 *
 * Constraints honored (FUL-10 §8 AC-1/AC-2/AC-3/AC-4):
 *  - All six templates available with correct defaults.
 *  - Every §5 field editable; validation rejects out-of-range bgOpacity, oversized
 *    text, and invalid position/animation/timestamp/duration.
 *  - All five show-moments selectable.
 *  - Pausa overlay exposes the full action set; minihook stays disabled until at.
 *
 * Security: text fields are plain-text inputs (no HTML); assetType "html" is
 * offered but the renderer disables it (the editor notes the text-only fallback).
 */

import type {
  Overlay,
  OverlayAction,
  OverlayContent,
  OverlayShowMoment,
  OverlayType,
  OverlayVisibility,
} from "../../contracts/src/contracts";
import {
  instantiateOverlay,
  OVERLAY_TEMPLATES,
} from "../../contracts/src/overlay-templates";
import {
  ACTION_LABELS,
  ACTIONS_BY_TYPE,
  FIELD_LIMITS,
  validateOverlay,
} from "./fullvsl-overlay";

const POSITION_GRID: ReadonlyArray<{
  pos: OverlayContent["position"];
  label: string;
  row: number;
  col: number;
}> = [
  { pos: "top_left", label: "↖", row: 0, col: 0 },
  { pos: "top", label: "↑", row: 0, col: 1 },
  { pos: "top_right", label: "↗", row: 0, col: 2 },
  { pos: undefined, label: "", row: 1, col: 0 },
  { pos: "center", label: "●", row: 1, col: 1 },
  { pos: undefined, label: "", row: 1, col: 2 },
  { pos: "bottom_left", label: "↙", row: 2, col: 0 },
  { pos: "bottom", label: "↓", row: 2, col: 1 },
  { pos: "bottom_right", label: "↘", row: 2, col: 2 },
];

const MOMENT_LABELS: Record<OverlayType, string> = {
  start: "Início",
  autoplay: "Reprodução automática",
  minihook: "Gancho durante o vídeo",
  pause: "Pausa",
  cta_reveal: "Exibição da CTA",
  end: "Final",
};

const MOMENT_ORDER: ReadonlyArray<OverlayType> = [
  "start",
  "autoplay",
  "minihook",
  "pause",
  "cta_reveal",
  "end",
];

const SHOW_MOMENTS: ReadonlyArray<{ value: OverlayShowMoment; label: string }> = [
  { value: "immediate", label: "Imediato" },
  { value: "timestamp", label: "Em um momento" },
  { value: "duration", label: "Por uma duração" },
  { value: "onPause", label: "Ao pausar" },
  { value: "onReplay", label: "Ao reiniciar / finalizar" },
];

const ANIMATIONS: ReadonlyArray<{ value: NonNullable<OverlayContent["animation"]>; label: string }> = [
  { value: "none", label: "Nenhuma" },
  { value: "fade", label: "Suave" },
  { value: "slide", label: "Deslizar" },
  { value: "zoom", label: "Zoom" },
];
const VISIBILITIES: ReadonlyArray<{ value: OverlayVisibility; label: string }> = [
  { value: "all", label: "Todos" },
  { value: "desktop", label: "Computador" },
  { value: "mobile", label: "Celular" },
];
const ASSET_TYPES: ReadonlyArray<{ value: "" | "image" | "video" | "html"; label: string }> = [
  { value: "", label: "Automático (pela URL)" },
  { value: "image", label: "Imagem" },
  { value: "video", label: "Vídeo / GIF" },
  { value: "html", label: "HTML (desativado — somente texto)" },
];

/** Editable moment entry: the overlay config + whether it is enabled. */
export interface OverlayMomentEntry {
  enabled: boolean;
  overlay: Overlay;
}

export interface OverlayEditorOptions {
  /** Host element the editor mounts into. */
  container: HTMLElement;
  /** Initial overlay set. Missing types are seeded from default templates (disabled). */
  overlays?: Overlay[];
  /** Fired with the enabled overlays whenever the config changes. */
  onChange: (overlays: Overlay[]) => void;
}

const EDITOR_CSS = `
.fvsl-ed{font:13px/1.5 "Inter",system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:#e7e9ee;display:flex;gap:14px;flex-wrap:wrap}
.fvsl-ed-moments{flex:0 0 200px;display:flex;flex-direction:column;gap:6px}
.fvsl-ed-moment{display:flex;align-items:center;gap:8px;padding:8px 10px;border:1px solid #2c3340;border-radius:8px;background:#161a21;cursor:pointer}
.fvsl-ed-moment.is-active{border-color:#008378;background:#102925}
.fvsl-ed-moment-name{flex:1 1 auto;font-weight:600}
.fvsl-ed-moment-type{opacity:.55;font-size:11px;text-transform:uppercase;letter-spacing:.04em}
.fvsl-ed-toggle{position:relative;width:34px;height:18px;flex:0 0 auto}
.fvsl-ed-toggle input{opacity:0;width:0;height:0;position:absolute}
.fvsl-ed-toggle span{position:absolute;inset:0;background:#3a4250;border-radius:10px;transition:background .15s ease}
.fvsl-ed-toggle span::after{content:"";position:absolute;left:2px;top:2px;width:14px;height:14px;border-radius:50%;background:#fff;transition:transform .15s ease}
.fvsl-ed-toggle input:checked + span{background:#008378}
.fvsl-ed-toggle input:checked + span::after{transform:translateX(16px)}
.fvsl-ed-form{flex:1 1 320px;min-width:0;display:flex;flex-direction:column;gap:14px}
.fvsl-ed-group{border:1px solid #232834;border-radius:10px;background:#161a21;padding:12px 14px}
.fvsl-ed-group-title{font-size:11px;text-transform:uppercase;letter-spacing:.05em;opacity:.6;margin:0 0 10px}
.fvsl-ed-fields{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.fvsl-ed-field{display:flex;flex-direction:column;gap:4px}
.fvsl-ed-field--full{grid-column:1 / -1}
.fvsl-ed-label{font-size:12px;opacity:.75}
.fvsl-ed-input,.fvsl-ed-select,.fvsl-ed-textarea{appearance:none;border:1px solid #2c3340;background:#0b0d11;color:#e7e9ee;border-radius:6px;padding:7px 9px;font:inherit;font-size:13px;width:100%;box-sizing:border-box}
.fvsl-ed-textarea{resize:vertical;min-height:40px}
.fvsl-ed-input:focus,.fvsl-ed-select:focus,.fvsl-ed-textarea:focus{outline:none;border-color:#008378}
.fvsl-ed-input.is-invalid{border-color:#e57373}
.fvsl-ed-hint{font-size:11px;opacity:.55}
.fvsl-ed-hint.is-error{opacity:1;color:#f0a0a0}
.fvsl-ed-grid{display:grid;grid-template-columns:repeat(3,46px);grid-template-rows:repeat(3,32px);gap:4px}
.fvsl-ed-pos{appearance:none;border:1px solid #2c3340;background:#0b0d11;color:#9aa3b2;border-radius:5px;cursor:pointer;font-size:14px;display:flex;align-items:center;justify-content:center}
.fvsl-ed-pos:disabled{visibility:hidden}
.fvsl-ed-pos.is-active{background:#00685f;border-color:#00685f;color:#fff}
.fvsl-ed-actions{display:flex;flex-wrap:wrap;gap:8px}
.fvsl-ed-chip{display:inline-flex;align-items:center;gap:6px;border:1px solid #2c3340;border-radius:16px;padding:4px 10px;background:#0b0d11;font-size:12px;cursor:pointer;user-select:none}
.fvsl-ed-chip input{accent-color:#008378}
.fvsl-ed-empty{padding:18px;text-align:center;opacity:.6}
@media (max-width:720px){.fvsl-ed{flex-direction:column}.fvsl-ed-moments{flex:1 1 auto}.fvsl-ed-fields{grid-template-columns:1fr}}
`;

let editorCssInjected = false;
function injectEditorCss(): void {
  if (editorCssInjected) return;
  editorCssInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-fullvsl-overlay-editor", "");
  style.textContent = EDITOR_CSS;
  document.head.appendChild(style);
}

/**
 * Seed the editable moment entries from the FUL-10 default templates, overlaying
 * any provided overlays by type. Missing types are added disabled.
 */
export function seedMoments(overlays?: Overlay[]): OverlayMomentEntry[] {
  const byType = new Map<OverlayType, Overlay>();
  for (const o of overlays ?? []) byType.set(o.type, o);
  return MOMENT_ORDER.map((type) => {
    const existing = byType.get(type);
    const overlay =
      existing ??
      instantiateOverlay(type, type === "minihook" ? { timing: { show: "timestamp", at: undefined as unknown as number } } : {});
    // minihook starts disabled (requires marketer-supplied timestamp).
    const defaultEnabled = type !== "minihook";
    return { enabled: existing != null ? true : defaultEnabled, overlay };
  });
}

/**
 * Form-based overlay configuration editor. Emits the enabled `Overlay[]` on every
 * field change for live player preview. Pure DOM, no runtime deps.
 */
export class OverlayEditor {
  private readonly container: HTMLElement;
  private readonly onChange: (overlays: Overlay[]) => void;
  private readonly root: HTMLElement;
  private readonly momentsList: HTMLElement;
  private readonly formHost: HTMLElement;
  private moments: OverlayMomentEntry[];
  private selectedType: OverlayType = "pause";

  constructor(opts: OverlayEditorOptions) {
    injectEditorCss();
    this.container = opts.container;
    this.onChange = opts.onChange;
    this.moments = seedMoments(opts.overlays);
    // Default-select the pause moment (richest action set).
    if (this.moments.find((m) => m.overlay.type === "pause")) this.selectedType = "pause";

    this.root = document.createElement("div");
    this.root.className = "fvsl-ed";

    this.momentsList = document.createElement("div");
    this.momentsList.className = "fvsl-ed-moments";

    this.formHost = document.createElement("div");
    this.formHost.className = "fvsl-ed-form";

    this.root.appendChild(this.momentsList);
    this.root.appendChild(this.formHost);
    this.container.appendChild(this.root);

    this.renderMoments();
    this.renderForm();
    this.emit();
  }

  /** The current editable moment entries (incl. disabled). */
  getEntries(): OverlayMomentEntry[] {
    return this.moments.map((m) => ({ ...m, overlay: structuredCloneSafe(m.overlay) }));
  }

  /** The enabled overlays (what the renderer consumes). */
  getOverlays(): Overlay[] {
    return this.moments.filter((m) => m.enabled).map((m) => structuredCloneSafe(m.overlay));
  }

  /** Replace the editor state from an external overlay set. */
  setOverlays(overlays: Overlay[]): void {
    this.moments = seedMoments(overlays);
    this.renderMoments();
    this.renderForm();
  }

  private emit(): void {
    this.onChange(this.getOverlays());
  }

  private entry(type: OverlayType): OverlayMomentEntry {
    const e = this.moments.find((m) => m.overlay.type === type);
    if (!e) throw new Error(`no moment for type ${type}`);
    return e;
  }

  /** Render the moment list with enable toggles + selection. */
  private renderMoments(): void {
    this.momentsList.innerHTML = "";
    for (const type of MOMENT_ORDER) {
      const entry = this.entry(type);
      const tpl = OVERLAY_TEMPLATES[type];
      const wrap = document.createElement("div");
      wrap.className = "fvsl-ed-moment";
      if (type === this.selectedType) wrap.classList.add("is-active");

      const toggle = document.createElement("label");
      toggle.className = "fvsl-ed-toggle";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = entry.enabled;
      cb.addEventListener("change", () => {
        entry.enabled = cb.checked;
        this.emit();
      });
      const track = document.createElement("span");
      toggle.appendChild(cb);
      toggle.appendChild(track);

      const name = document.createElement("span");
      name.className = "fvsl-ed-moment-name";
      name.textContent = MOMENT_LABELS[type];
      const sub = document.createElement("span");
      sub.className = "fvsl-ed-moment-type";
      sub.textContent = tpl.timing.show;

      wrap.appendChild(toggle);
      wrap.appendChild(name);
      wrap.appendChild(sub);
      wrap.addEventListener("click", (ev) => {
        // Don't select when clicking the toggle itself.
        if ((ev.target as HTMLElement).closest(".fvsl-ed-toggle")) return;
        this.selectedType = type;
        this.renderMoments();
        this.renderForm();
      });
      this.momentsList.appendChild(wrap);
    }
  }

  /** Render the detail form for the selected moment. */
  private renderForm(): void {
    this.formHost.innerHTML = "";
    const entry = this.entry(this.selectedType);
    if (!entry.enabled) {
      const note = document.createElement("div");
      note.className = "fvsl-ed-empty";
      note.textContent = `${MOMENT_LABELS[this.selectedType]} está desativado. Ative-o na lista para editar.`;
      this.formHost.appendChild(note);
      return;
    }
    this.formHost.appendChild(this.renderContentGroup(entry));
    this.formHost.appendChild(this.renderAppearanceGroup(entry));
    this.formHost.appendChild(this.renderTimingGroup(entry));
    this.formHost.appendChild(this.renderActionsGroup(entry));
  }

  /** Conteúdo fields: text, subtext, button label/action, asset URL + type. */
  private renderContentGroup(entry: OverlayMomentEntry): HTMLElement {
    const group = document.createElement("div");
    group.className = "fvsl-ed-group";
    const title = document.createElement("p");
    title.className = "fvsl-ed-group-title";
    title.textContent = "Conteúdo";
    const fields = document.createElement("div");
    fields.className = "fvsl-ed-fields";

    const c = entry.overlay.content;
    fields.appendChild(this.textRow(entry, "text", "Texto principal", c.text, FIELD_LIMITS.text));
    fields.appendChild(this.textRow(entry, "subtext", "Subtexto", c.subtext, FIELD_LIMITS.subtext));
    fields.appendChild(this.textRow(entry, "buttonLabel", "Texto do botão", c.buttonLabel, FIELD_LIMITS.buttonLabel));

    // Button action selector.
    const actionField = document.createElement("div");
    actionField.className = "fvsl-ed-field";
    const actionLabel = document.createElement("span");
    actionLabel.className = "fvsl-ed-label";
    actionLabel.textContent = "Ação principal do botão";
    const actionSelect = document.createElement("select");
    actionSelect.className = "fvsl-ed-select";
    for (const action of ACTIONS_BY_TYPE[this.selectedType]) {
      const opt = document.createElement("option");
      opt.value = action;
      opt.textContent = ACTION_LABELS[action];
      actionSelect.appendChild(opt);
    }
    actionSelect.value = (c.buttonAction as OverlayAction) ?? entry.overlay.actions[0] ?? "continue";
    actionSelect.addEventListener("change", () => {
      entry.overlay.content.buttonAction = actionSelect.value;
      this.touch(entry);
    });
    actionField.appendChild(actionLabel);
    actionField.appendChild(actionSelect);
    fields.appendChild(actionField);

    // Asset URL.
    const assetField = document.createElement("div");
    assetField.className = "fvsl-ed-field fvsl-ed-field--full";
    const assetLabel = document.createElement("span");
    assetLabel.className = "fvsl-ed-label";
    assetLabel.textContent = "URL do arquivo personalizado (imagem / vídeo / GIF)";
    const assetInput = document.createElement("input");
    assetInput.className = "fvsl-ed-input";
    assetInput.type = "url";
    assetInput.inputMode = "url";
    assetInput.placeholder = "https://cdn.example.com/offer.png";
    assetInput.value = c.assetUrl ?? "";
    assetInput.addEventListener("input", () => {
      entry.overlay.content.assetUrl = assetInput.value.trim() || undefined;
      this.touch(entry);
    });
    assetField.appendChild(assetLabel);
    assetField.appendChild(assetInput);

    // Tipo de arquivo selector.
    const typeField = document.createElement("div");
    typeField.className = "fvsl-ed-field";
    const typeLabel = document.createElement("span");
    typeLabel.className = "fvsl-ed-label";
    typeLabel.textContent = "Tipo de arquivo";
    const typeSelect = document.createElement("select");
    typeSelect.className = "fvsl-ed-select";
    for (const t of ASSET_TYPES) {
      const opt = document.createElement("option");
      opt.value = t.value;
      opt.textContent = t.label;
      typeSelect.appendChild(opt);
    }
    typeSelect.value = c.assetType ?? "";
    typeSelect.addEventListener("change", () => {
      const v = typeSelect.value as "" | "image" | "video" | "html";
      entry.overlay.content.assetType = v === "" ? undefined : v;
      this.touch(entry);
    });
    typeField.appendChild(typeLabel);
    typeField.appendChild(typeSelect);
    assetField.appendChild(typeField);

    fields.appendChild(assetField);

    group.appendChild(title);
    group.appendChild(fields);
    return group;
  }

  /** Aparência fields: position grid, colors, opacity, animation, visibility. */
  private renderAppearanceGroup(entry: OverlayMomentEntry): HTMLElement {
    const group = document.createElement("div");
    group.className = "fvsl-ed-group";
    const title = document.createElement("p");
    title.className = "fvsl-ed-group-title";
    title.textContent = "Aparência";
    const fields = document.createElement("div");
    fields.className = "fvsl-ed-fields";

    // Position picker (3×3 safe-zone grid).
    const posField = document.createElement("div");
    posField.className = "fvsl-ed-field";
    const posLabel = document.createElement("span");
    posLabel.className = "fvsl-ed-label";
    posLabel.textContent = "Posição (área segura)";
    const grid = document.createElement("div");
    grid.className = "fvsl-ed-grid";
    const current = entry.overlay.content.position ?? "center";
    for (const cell of POSITION_GRID) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "fvsl-ed-pos";
      btn.textContent = cell.label;
      if (cell.pos === undefined) {
        btn.disabled = true;
      } else {
        if (cell.pos === current) btn.classList.add("is-active");
        btn.title = cell.pos;
        btn.addEventListener("click", () => {
          entry.overlay.content.position = cell.pos;
          this.touch(entry);
          this.renderForm();
        });
      }
      grid.appendChild(btn);
    }
    posField.appendChild(posLabel);
    posField.appendChild(grid);
    fields.appendChild(posField);

    // bgOpacity slider.
    const opField = document.createElement("div");
    opField.className = "fvsl-ed-field";
    const opLabel = document.createElement("span");
    opLabel.className = "fvsl-ed-label";
    opLabel.textContent = "Opacidade do fundo";
    const opRow = document.createElement("div");
    opRow.style.display = "flex";
    opRow.style.alignItems = "center";
    opRow.style.gap = "8px";
    const opInput = document.createElement("input");
    opInput.className = "fvsl-ed-input";
    opInput.type = "range";
    opInput.min = "0";
    opInput.max = "1";
    opInput.step = "0.05";
    opInput.value = String(entry.overlay.content.bgOpacity ?? 0.55);
    const opVal = document.createElement("span");
    opVal.className = "fvsl-ed-hint";
    opVal.textContent = opInput.value;
    opInput.addEventListener("input", () => {
      entry.overlay.content.bgOpacity = Number(opInput.value);
      opVal.textContent = opInput.value;
      this.touch(entry);
    });
    opRow.appendChild(opInput);
    opRow.appendChild(opVal);
    opField.appendChild(opLabel);
    opField.appendChild(opRow);
    fields.appendChild(opField);

    // Animação selector.
    const animField = this.selectRow(entry, "animation", "Animação", ANIMATIONS, entry.overlay.content.animation ?? "fade", (v) => {
      entry.overlay.content.animation = v as OverlayContent["animation"];
    });
    fields.appendChild(animField);

    // Visibility selector.
    const visField = this.selectRow(entry, "visibility", "Exibir no dispositivo", VISIBILITIES, entry.overlay.content.visibility ?? "all", (v) => {
      entry.overlay.content.visibility = v as OverlayVisibility;
    });
    fields.appendChild(visField);

    // Color inputs (text / button).
    fields.appendChild(this.colorRow(entry, "Cor do botão", entry.overlay.content.colors?.button ?? "#00685f", (v) => {
      entry.overlay.content.colors = { ...entry.overlay.content.colors, button: v };
    }));
    fields.appendChild(this.colorRow(entry, "Cor do texto do botão", entry.overlay.content.colors?.buttonText ?? "#ffffff", (v) => {
      entry.overlay.content.colors = { ...entry.overlay.content.colors, buttonText: v };
    }));

    group.appendChild(title);
    group.appendChild(fields);
    return group;
  }

  /** Momento fields: show moment, timestamp at, duration ms. */
  private renderTimingGroup(entry: OverlayMomentEntry): HTMLElement {
    const group = document.createElement("div");
    group.className = "fvsl-ed-group";
    const title = document.createElement("p");
    title.className = "fvsl-ed-group-title";
    title.textContent = "Momento";
    const fields = document.createElement("div");
    fields.className = "fvsl-ed-fields";

    // Momento de exibição (some types lock the moment — start/autoplay/end/pause/cta_reveal).
    const locked = this.timingLockedForType(this.selectedType);
    const momentField = this.selectRow(entry, "show", "Momento de exibição", SHOW_MOMENTS.map((m) => ({ value: m.value, label: m.label })), entry.overlay.timing.show, (v) => {
      entry.overlay.timing.show = v as OverlayShowMoment;
    });
    if (locked) {
      const sel = momentField.querySelector("select") as HTMLSelectElement;
      sel.disabled = true;
      const hint = document.createElement("span");
      hint.className = "fvsl-ed-hint";
      hint.textContent = "Bloqueado para este tipo de momento";
      momentField.appendChild(hint);
    }
    fields.appendChild(momentField);

    // Timestamp `at`.
    const atField = document.createElement("div");
    atField.className = "fvsl-ed-field";
    const atLabel = document.createElement("span");
    atLabel.className = "fvsl-ed-label";
    atLabel.textContent = "Exibir em (segundos)";
    const atInput = document.createElement("input");
    atInput.className = "fvsl-ed-input";
    atInput.type = "number";
    atInput.min = "0";
    atInput.step = "1";
    atInput.value = entry.overlay.timing.at != null ? String(entry.overlay.timing.at) : "";
    const atHint = document.createElement("span");
    atHint.className = "fvsl-ed-hint";
    atHint.textContent = entry.overlay.timing.show === "timestamp" ? "Obrigatório para momento específico" : "Usado somente para momento específico";
    atInput.addEventListener("input", () => {
      const n = Number(atInput.value);
      entry.overlay.timing.at = atInput.value === "" ? undefined : Math.max(0, n);
      this.touch(entry, { at: atInput, atHint });
    });
    atField.appendChild(atLabel);
    atField.appendChild(atInput);
    atField.appendChild(atHint);
    fields.appendChild(atField);

    // Duration ms.
    const durField = document.createElement("div");
    durField.className = "fvsl-ed-field";
    const durLabel = document.createElement("span");
    durLabel.className = "fvsl-ed-label";
    durLabel.textContent = "Duração (ms)";
    const durInput = document.createElement("input");
    durInput.className = "fvsl-ed-input";
    durInput.type = "number";
    durInput.min = "0";
    durInput.step = "100";
    durInput.value = entry.overlay.timing.durationMs != null ? String(entry.overlay.timing.durationMs) : "";
    durInput.addEventListener("input", () => {
      const n = Number(durInput.value);
      entry.overlay.timing.durationMs = durInput.value === "" ? undefined : Math.max(0, n);
      this.touch(entry);
    });
    durField.appendChild(durLabel);
    durField.appendChild(durInput);
    fields.appendChild(durField);

    group.appendChild(title);
    group.appendChild(fields);
    return group;
  }

  /** Ações group: toggle chips per available action. */
  private renderActionsGroup(entry: OverlayMomentEntry): HTMLElement {
    const group = document.createElement("div");
    group.className = "fvsl-ed-group";
    const title = document.createElement("p");
    title.className = "fvsl-ed-group-title";
    title.textContent = "Ações";
    const actions = document.createElement("div");
    actions.className = "fvsl-ed-actions";
    for (const action of ACTIONS_BY_TYPE[this.selectedType]) {
      const chip = document.createElement("label");
      chip.className = "fvsl-ed-chip";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = entry.overlay.actions.includes(action);
      cb.addEventListener("change", () => {
        const set = new Set(entry.overlay.actions);
        if (cb.checked) set.add(action);
        else set.delete(action);
        // Keep at least one action.
        entry.overlay.actions = set.size > 0 ? (ACTIONS_BY_TYPE[this.selectedType].filter((a) => set.has(a)) as OverlayAction[]) : [action];
        this.touch(entry);
      });
      chip.appendChild(cb);
      chip.appendChild(document.createTextNode(ACTION_LABELS[action]));
      actions.appendChild(chip);
    }
    const hint = document.createElement("span");
    hint.className = "fvsl-ed-hint";
    hint.textContent = "É necessário selecionar pelo menos uma ação.";
    group.appendChild(title);
    group.appendChild(actions);
    group.appendChild(hint);
    return group;
  }

  /** A labeled text input row with char-limit validation hint. */
  private textRow(
    entry: OverlayMomentEntry,
    field: "text" | "subtext" | "buttonLabel",
    label: string,
    value: string | undefined,
    limit: number,
  ): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "fvsl-ed-field";
    const lbl = document.createElement("span");
    lbl.className = "fvsl-ed-label";
    lbl.textContent = label;
    const input = document.createElement("input");
    input.className = "fvsl-ed-input";
    input.type = "text";
    input.value = value ?? "";
    input.maxLength = limit;
    const hint = document.createElement("span");
    hint.className = "fvsl-ed-hint";
    const updateHint = (): void => {
      const len = input.value.length;
      hint.textContent = `${len} / ${limit}`;
      hint.classList.toggle("is-error", len > limit);
      input.classList.toggle("is-invalid", len > limit);
    };
    updateHint();
    input.addEventListener("input", () => {
      entry.overlay.content[field] = input.value || undefined;
      updateHint();
      this.touch(entry);
    });
    wrap.appendChild(lbl);
    wrap.appendChild(input);
    wrap.appendChild(hint);
    return wrap;
  }

  /** A labeled select row. */
  private selectRow(
    entry: OverlayMomentEntry,
    _key: string,
    label: string,
    options: ReadonlyArray<{ value: string; label: string }>,
    current: string,
    onApply: (v: string) => void,
  ): HTMLElement {
    void _key;
    const wrap = document.createElement("div");
    wrap.className = "fvsl-ed-field";
    const lbl = document.createElement("span");
    lbl.className = "fvsl-ed-label";
    lbl.textContent = label;
    const sel = document.createElement("select");
    sel.className = "fvsl-ed-select";
    for (const o of options) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.textContent = o.label;
      sel.appendChild(opt);
    }
    sel.value = current;
    sel.addEventListener("change", () => {
      onApply(sel.value);
      this.touch(entry);
    });
    wrap.appendChild(lbl);
    wrap.appendChild(sel);
    return wrap;
  }

  /** A labeled color input row. */
  private colorRow(entry: OverlayMomentEntry, label: string, current: string, onApply: (v: string) => void): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "fvsl-ed-field";
    const lbl = document.createElement("span");
    lbl.className = "fvsl-ed-label";
    lbl.textContent = label;
    const input = document.createElement("input");
    input.className = "fvsl-ed-input";
    input.type = "color";
    input.value = this.toColorInput(current);
    input.addEventListener("input", () => {
      onApply(input.value);
      this.touch(entry);
    });
    wrap.appendChild(lbl);
    wrap.appendChild(input);
    return wrap;
  }

  /** Normalize a hex/rgba color to a hex value the color input accepts. */
  private toColorInput(color: string): string {
    if (/^#[0-9a-f]{6}$/i.test(color)) return color;
    if (/^#[0-9a-f]{3}$/i.test(color)) {
      return "#" + color.slice(1).split("").map((c) => c + c).join("");
    }
    return "#00685f";
  }

  /** Some overlay types lock the show-moment (start/autoplay/pause/end/cta_reveal). */
  private timingLockedForType(type: OverlayType): boolean {
    return type !== "minihook";
  }

  /**
   * Validate + emit after an edit. Surfaces validation errors inline for the
   * timestamp field (the most common missing-required case).
   */
  private touch(entry: OverlayMomentEntry, extra?: { at?: HTMLInputElement; atHint?: HTMLElement }): void {
    const errors = validateOverlay(entry.overlay);
    if (extra?.at && extra?.atHint) {
      if (errors.at) {
        extra.at.classList.add("is-invalid");
        extra.atHint.textContent = errors.at[0];
        extra.atHint.classList.add("is-error");
      } else {
        extra.at.classList.remove("is-invalid");
        extra.atHint.textContent = entry.overlay.timing.show === "timestamp" ? "Obrigatório para momento específico" : "Usado somente para momento específico";
        extra.atHint.classList.remove("is-error");
      }
    }
    this.emit();
  }
}

/** structuredClone polyfill-safe deep clone for plain overlay data. */
function structuredCloneSafe<T>(value: T): T {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}
